import { defineStore } from 'pinia';
import axios from 'axios';
import Cookies from 'js-cookie';

const getStoredAuth = () => {
    let token = Cookies.get('token');
    let userStr = Cookies.get('user');

    if (!token && typeof localStorage !== 'undefined') {
        token = localStorage.getItem('token');
    }
    if (!userStr && typeof localStorage !== 'undefined') {
        userStr = localStorage.getItem('user');
    }

    // Если кука или токен отсутствуют, сбрасываем данные
    if (!token || !userStr) {
        Cookies.remove('user');
        Cookies.remove('token');
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('user');
            localStorage.removeItem('token');
        }
        delete axios.defaults.headers.common['Authorization'];
        return { user: null, token: null };
    }

    try {
        const user = JSON.parse(userStr);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return { user, token };
    } catch (e) {
        Cookies.remove('user');
        Cookies.remove('token');
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('user');
            localStorage.removeItem('token');
        }
        delete axios.defaults.headers.common['Authorization'];
        return { user: null, token: null };
    }
};

const initialAuth = getStoredAuth();

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: initialAuth.user,
        token: initialAuth.token,
        isLoaded: false,
        loading: false,
        error: null,
    }),

    getters: {
        isAuthenticated: (state) => !!state.user && !!state.token,
        userName: (state) => state.user?.name || '',
        userEmail: (state) => state.user?.email || '',
        userRole: (state) => state.user?.role || 'guest',
        userId: (state) => state.user?.id || null
    },

    actions: {
        setUser(userData) {
            this.user = userData;
            if (userData) {
                const userJson = JSON.stringify(userData);
                Cookies.set('user', userJson, {
                    expires: 7,
                    sameSite: 'lax',
                    secure: window.location.protocol === 'https:'
                });
                if (typeof localStorage !== 'undefined') {
                    localStorage.setItem('user', userJson);
                }
            } else {
                Cookies.remove('user');
                if (typeof localStorage !== 'undefined') {
                    localStorage.removeItem('user');
                }
            }
        },

        async fetchUser() {
            if (!this.token) return null;
            try {
                const response = await axios.get('/api/user');
                if (response.data && response.data.id) {
                    this.setUser(response.data);
                    this.isLoaded = true;
                    return response.data;
                }
            } catch (err) {
                console.warn('Ошибка загрузки данных пользователя:', err);
            }
            return null;
        },

        setToken(tokenData) {
            this.token = tokenData;
            if (tokenData) {
                Cookies.set('token', tokenData, {
                    expires: 7,
                    sameSite: 'lax',
                    secure: window.location.protocol === 'https:'
                });
                if (typeof localStorage !== 'undefined') {
                    localStorage.setItem('token', tokenData);
                }
                axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
            } else {
                Cookies.remove('token');
                if (typeof localStorage !== 'undefined') {
                    localStorage.removeItem('token');
                }
                delete axios.defaults.headers.common['Authorization'];
            }
        },

        clearAuthData() {
            this.user = null;
            this.token = null;
            Cookies.remove('user');
            Cookies.remove('token');
            if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('user');
                localStorage.removeItem('token');
            }
            sessionStorage.clear();
            delete axios.defaults.headers.common['Authorization'];
        },

        async logout() {
            try {
                if (this.token) {
                    await axios.post('/api/logout');
                }
            } catch (error) {
                console.warn('Ошибка при выходе на сервере:', error);
            } finally {
                this.clearAuthData();
            }
        },

        async fetchSecurityStatus() {
            try {
                const response = await axios.get('/api/user/security/status');
                if (response.data.result === 'good') {
                    return { success: true, data: response.data.data };
                }
                return { success: false, error: response.data.error || 'Ошибка загрузки статуса безопасности' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка сети' };
            }
        },

        async changePassword(payload) {
            try {
                const response = await axios.post('/api/user/security/change-password', payload);
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                }
                return { success: false, error: response.data.error || 'Ошибка смены пароля' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || err.response?.data?.message || 'Ошибка смены пароля' };
            }
        },

        async sendEmailVerification() {
            try {
                const response = await axios.post('/api/user/security/send-verification');
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message, dev_code: response.data.dev_code };
                }
                return { success: false, error: response.data.error || 'Ошибка отправки кода' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка сети' };
            }
        },

        async verifyEmailCode(code) {
            try {
                const response = await axios.post('/api/user/security/verify-email', { code });
                if (response.data.result === 'good') {
                    if (this.user) {
                        this.user.email_verified_at = new Date().toISOString();
                        this.setUser(this.user);
                    }
                    return { success: true, message: response.data.message };
                }
                return { success: false, error: response.data.error || 'Неверный код' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка подтверждения' };
            }
        },

        async toggleTwoFactor(enabled) {
            try {
                const response = await axios.post('/api/user/security/2fa/toggle', { enabled });
                if (response.data.result === 'good') {
                    if (this.user) {
                        this.user.two_factor_enabled = response.data.two_factor_enabled;
                        this.setUser(this.user);
                    }
                    return { success: true, message: response.data.message, two_factor_enabled: response.data.two_factor_enabled };
                }
                return { success: false, error: response.data.error || 'Ошибка переключения 2FA' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка сети' };
            }
        },

        async login(email, password, twoFactorCode = null) {
            this.loading = true;
            this.error = null;

            try {
                const payload = {
                    email: email,
                    password: password
                };
                if (twoFactorCode) {
                    payload.two_factor_code = twoFactorCode;
                }

                const response = await axios.post('/api/login', payload);

                if (response.data.result === '2fa_required') {
                    return {
                        success: false,
                        two_factor_required: true,
                        message: response.data.message,
                        dev_code: response.data.dev_code
                    };
                }

                if (response.data.result === 'auth') {
                    this.setToken(response.data.token);
                    this.setUser(response.data.user);
                    return { success: true, user: this.user, role: false };
                }
                else if (response.data.result === 'role') {
                    this.setToken(response.data.token);
                    this.setUser(response.data.user);
                    return { success: true, role: true };
                }
                else {
                    this.error = response.data.message || 'Ошибка входа';
                    return { success: false, error: this.error };
                }
            } catch (error) {
                this.error = error.response?.data?.message || 'Ошибка соединения';
                return { success: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        async register(formData) {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.post('/api/signup', {
                    name: formData.firstName,
                    surname: formData.lastName,
                    email: formData.email,
                    password: formData.password,
                    password_repeat: formData.confirmPassword
                });

                if (response.data.result === 'good') {
                    return await this.login(formData.email, formData.password);
                } else {
                    this.error = response.data.message || 'Ошибка регистрации';
                    return { success: false, error: this.error };
                }
            } catch (error) {
                this.error = error.response?.data?.message || 'Ошибка соединения';
                return { success: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        async selectRole(role) {
            this.loading = true;
            this.error = null;
            try {
                const response = await axios.post('/api/ChooseRole', { role: Number(role) });

                if (response.data.result === 'good') {
                    if (response.data.user) {
                        this.setUser(response.data.user);
                    } else if (this.user) {
                        this.setUser({ ...this.user, role_id: Number(role) });
                    }
                    return { success: true, user: this.user, roleId: Number(role) };
                } else {
                    this.error = response.data.message || 'Ошибка выбора роли';
                    return { success: false, error: this.error };
                }
            } catch (error) {
                this.error = error.response?.data?.message || 'Ошибка соединения при выборе роли';
                return { success: false, error: this.error };
            } finally {
                this.loading = false;
            }
        },

        async BindCode(code) {
            const response = await axios.post('/api/tenant/BindCode', {
                code: code
            });

            if (response.data.result == 'good') {
                return { success: true, object: response.data.object }
            }
            else {
                this.error = response.data.message || 'Ошибка привязки';
                return { success: false, error: this.error };
            }
        },

        async fetchTenantObjects() {
            const response = await axios.post('/api/tenant/fetchTenantObjects');

            if (response.data.result == 'good')
                return { success: true, objects: response.data.objects };
            else {
                this.error = response.data.message || response.data.error || 'Ошибка получения объектов';
                return { success: false, error: this.error };
            }
        },

        async createTenantCustomObject(formData) {
            try {
                let body = formData;
                let headers = {};
                if (formData instanceof FormData) {
                    headers['Content-Type'] = 'multipart/form-data';
                }
                const response = await axios.post('/api/tenant/createCustomObject', body, { headers });
                if (response.data.result === 'good') {
                    return { success: true, link: response.data.link };
                }
                return { success: false, error: response.data.error || 'Ошибка при добавлении объекта' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка соединения с сервером' };
            }
        },

        async updateTenantCustomObject(formData) {
            try {
                let body = formData;
                let headers = {};
                if (formData instanceof FormData) {
                    headers['Content-Type'] = 'multipart/form-data';
                }
                const response = await axios.post('/api/tenant/updateCustomObject', body, { headers });
                if (response.data.result === 'good') {
                    return { success: true, link: response.data.link };
                }
                return { success: false, error: response.data.error || 'Ошибка при обновлении объекта' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка соединения с сервером' };
            }
        },

        async deleteTenantCustomObject(linkId) {
            try {
                const response = await axios.post('/api/tenant/deleteCustomObject', { link_id: linkId });
                if (response.data.result === 'good') {
                    return { success: true };
                }
                return { success: false, error: response.data.error || 'Ошибка при удалении объекта' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка соединения с сервером' };
            }
        },

        async fetchInviteDetails(token) {
            try {
                const response = await axios.get(`/api/invite/${token}`);
                if (response.data.result === 'good') {
                    return { success: true, invite: response.data.invite };
                }
                return { success: false, error: response.data.error || 'Приглашение не найдено' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка загрузки приглашения' };
            }
        },

        async acceptLandlordInvite(token) {
            try {
                const response = await axios.post('/api/landlord/acceptInvite', { token });
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message, object_id: response.data.object_id };
                }
                return { success: false, error: response.data.error || 'Не удалось принять приглашение' };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка соединения' };
            }
        },

        async fetchTenants() {
            try {
                const response = await axios.post('/api/landlord/fetchTenants');

                if (response.data.result === 'good')
                    return {
                        success: true,
                        tenants: response.data.tenants || [],
                        requests: response.data.requests || [],
                        archive: response.data.archive || []
                    };
                else {
                    this.error = response.data.message || response.data.error || 'Ошибка получения арендаторов';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || err.response?.data?.message || 'Ошибка получения арендаторов';
                return { success: false, error: this.error };
            }
        },

        async addTenant(payload) {
            try {
                const response = await axios.post('/api/landlord/addTenant', payload);
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message, link: response.data.link };
                } else {
                    return { success: false, error: response.data.error || response.data.message };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || err.response?.data?.message || 'Ошибка добавления арендатора' };
            }
        },

        async approveTenant(payload) {
            try {
                const data = typeof payload === 'object' && payload !== null ? payload : { id: payload };
                const response = await axios.post('/api/landlord/approveTenant', data);
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                } else {
                    return { success: false, error: response.data.error || response.data.message };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || err.response?.data?.message || 'Ошибка подтверждения заявки' };
            }
        },

        async rejectTenant(id) {
            try {
                const response = await axios.post('/api/landlord/rejectTenant', { id });
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                } else {
                    return { success: false, error: response.data.error || response.data.message };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || err.response?.data?.message || 'Ошибка отклонения заявки' };
            }
        },

        async terminateTenant(id) {
            try {
                const response = await axios.post('/api/landlord/terminateTenant', { id });
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                } else {
                    return { success: false, error: response.data.error || response.data.message };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || err.response?.data?.message || 'Ошибка завершения аренды' };
            }
        },

        async fetchLandlords() {
            const response = await axios.post('/api/tenant/fetchLandlords');

            if (response.data.result == 'good')
                return { success: true, landlords: response.data.landlords };
            else {
                this.error = response.data.message || response.data.error || 'Ошибка получения арендодателей';
                return { success: false, error: this.error };
            }
        },

        async fetchEmployees() {
            try {
                const response = await axios.post('/api/tenant/fetchEmployees');

                if (response.data.result === 'good')
                    return { success: true, employees: response.data.employees };
                else {
                    this.error = response.data.message || response.data.error || 'Ошибка получения сотрудников';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка получения сотрудников';
                return { success: false, error: this.error };
            }
        },

        async createEmployee(form) {
            try {
                const response = await axios.post('/api/tenant/createEmployee', form);

                if (response.data.result === 'good')
                    return { success: true, employee: response.data.employee };
                else {
                    this.error = response.data.message || response.data.error || 'Ошибка добавления сотрудника';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка добавления сотрудника';
                return { success: false, error: this.error };
            }
        },

        async updateEmployee(form) {
            try {
                const response = await axios.post('/api/tenant/updateEmployee', form);

                if (response.data.result === 'good')
                    return { success: true, employee: response.data.employee };
                else {
                    this.error = response.data.message || response.data.error || 'Ошибка обновления сотрудника';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка обновления сотрудника';
                return { success: false, error: this.error };
            }
        },

        async deleteEmployee(id) {
            try {
                const response = await axios.post('/api/tenant/deleteEmployee', { id });

                if (response.data.result === 'good')
                    return { success: true };
                else {
                    this.error = response.data.message || response.data.error || 'Ошибка удаления сотрудника';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка удаления сотрудника';
                return { success: false, error: this.error };
            }
        },

        async getEmployeeDetails(id) {
            try {
                const response = await axios.post('/api/tenant/getEmployeeDetails', { id });

                if (response.data.result === 'good')
                    return { success: true, details: response.data.details };
                else {
                    this.error = response.data.message || response.data.error || 'Ошибка получения данных сотрудника';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка получения данных сотрудника';
                return { success: false, error: this.error };
            }
        },

        async updateEmployeeDetails(form) {
            try {
                const response = await axios.post('/api/tenant/updateEmployeeDetails', form);

                if (response.data.result === 'good')
                    return { success: true, details: response.data.details };
                else {
                    this.error = response.data.message || response.data.error || 'Ошибка обновления данных сотрудника';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка обновления данных сотрудника';
                return { success: false, error: this.error };
            }
        },

        async calculateSalaryPreview(params) {
            try {
                const response = await axios.post('/api/tenant/calculateSalaryPreview', params);
                if (response.data.result === 'good') {
                    return { success: true, calculation: response.data.calculation };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка расчета ЗП' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка расчета ЗП' };
            }
        },

        async generateEmployeePassword() {
            try {
                const response = await axios.post('/api/tenant/generateEmployeePassword');
                if (response.data.result === 'good') {
                    return { success: true, password: response.data.password };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка генерации пароля' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка генерации пароля' };
            }
        },

        async createTicket(form) {
            try {
                const formData = new FormData();
                if (form.category) formData.append('category', form.category);
                if (form.title) formData.append('title', form.title);
                if (form.message) formData.append('message', form.message);
                if (form.landlordId) formData.append('landlordId', form.landlordId);
                if (form.objectId) formData.append('objectId', form.objectId);
                if (form.file) formData.append('file', form.file);

                const response = await axios.post('/api/tenant/createTicket', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (response.data.result === 'good')
                    return { success: true, ticket: response.data.ticket };
                else {
                    this.error = response.data.error || 'Ошибка создания обращения';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка создания обращения';
                return { success: false, error: this.error };
            }
        },

        async fetchTickets(payload = {}) {
            try {
                const isTenantRequest = payload.as === 'tenant' || (this.user?.role_id != 1 && payload.as !== 'landlord');
                const url = isTenantRequest ? '/api/tenant/fetchTickets' : '/api/landlord/fetchTickets';
                const response = await axios.post(url, payload);
                if (response.data.result === 'good')
                    return { success: true, tickets: response.data.tickets || [] };
                else {
                    this.error = response.data.error || 'Ошибка получения обращений';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка получения обращений';
                return { success: false, error: this.error };
            }
        },

        async fetchLandlordTickets() {
            try {
                const response = await axios.post('/api/landlord/fetchTickets');
                if (response.data.result === 'good')
                    return { success: true, tickets: response.data.tickets };
                else {
                    this.error = response.data.error || 'Ошибка получения обращений';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка получения обращений';
                return { success: false, error: this.error };
            }
        },

        async replyTicket(form) {
            try {
                const formData = new FormData();
                formData.append('ticketId', form.ticketId);
                formData.append('message', form.message);
                if (form.file) formData.append('file', form.file);

                const response = await axios.post('/api/tenant/replyTicket', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message, ticket_status: response.data.ticket_status };
                } else {
                    this.error = response.data.error || 'Ошибка отправки ответа';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка отправки ответа';
                return { success: false, error: this.error };
            }
        },

        async updateTicketStatus(ticketId, status) {
            try {
                const response = await axios.post('/api/tenant/updateTicketStatus', { ticketId, status });
                if (response.data.result === 'good') {
                    return { success: true, ticket: response.data.ticket };
                } else {
                    this.error = response.data.error || 'Ошибка обновления статуса';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка обновления статуса';
                return { success: false, error: this.error };
            }
        },

        // ─── Оплата аренды (Арендатор) ──────────────────────────────────────
        async fetchTenantRentPayments() {
            try {
                const response = await axios.get('/api/tenant/rentPayments');
                if (response.data.result === 'good') {
                    return { success: true, payments: response.data.payments || [], links: response.data.links || [] };
                } else {
                    this.error = response.data.error || 'Ошибка загрузки платежей по аренде';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка загрузки платежей по аренде';
                return { success: false, error: this.error };
            }
        },

        async submitRentPayment(formData) {
            try {
                const response = await axios.post('/api/tenant/submitRentPayment', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (response.data.result === 'good') {
                    return { success: true, payment: response.data.payment, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка отправки подтверждения оплаты';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка отправки подтверждения оплаты';
                return { success: false, error: this.error };
            }
        },

        // ─── Оплата аренды (Арендодатель) ───────────────────────────────────
        async fetchLandlordRentPayments() {
            try {
                const response = await axios.get('/api/landlord/rentPayments');
                if (response.data.result === 'good') {
                    return {
                        success: true,
                        payments: response.data.payments || [],
                        pending_count: response.data.pending_count || 0
                    };
                } else {
                    this.error = response.data.error || 'Ошибка загрузки платежей арендаторов';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка загрузки платежей арендаторов';
                return { success: false, error: this.error };
            }
        },

        async approveRentPayment(paymentId) {
            try {
                const response = await axios.post('/api/landlord/approveRentPayment', { payment_id: paymentId });
                if (response.data.result === 'good') {
                    return { success: true, payment: response.data.payment, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка подтверждения оплаты';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка подтверждения оплаты';
                return { success: false, error: this.error };
            }
        },

        async rejectRentPayment(paymentId, comment) {
            try {
                const response = await axios.post('/api/landlord/rejectRentPayment', { payment_id: paymentId, comment });
                if (response.data.result === 'good') {
                    return { success: true, payment: response.data.payment, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка отклонения оплаты';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка отклонения оплаты';
                return { success: false, error: this.error };
            }
        },

        async updateContractDetails(form) {
            try {
                const response = await axios.post('/api/landlord/updateContractDetails', form);
                if (response.data.result === 'good') {
                    return { success: true, link: response.data.link, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка сохранения договора';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка сохранения договора';
                return { success: false, error: this.error };
            }
        },


        //Логика объектов арендатора
        async AddObjects(form) {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.post('/api/landlord/AddObject', { form: form });

                if (response.data.result === 'good') {
                    return { success: true, object: response.data.object };
                } else {
                    this.error = response.data.message || 'Ошибка добавления объекта';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Ошибка добавления объекта';
                this.error = msg;
                return { success: false, error: msg, errors: err.response?.data?.errors };
            } finally {
                this.loading = false;
            }
        },

        async fetchObjects() {
            try {
                const response = await axios.post('/api/landlord/fetchObjects');

                if (response.data.result === 'good')
                    return { success: true, objects: response.data.objects };
                else {
                    this.error = response.data.message || 'Ошибка получения объектов';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.message || 'Ошибка сети при получении объектов';
                return { success: false, error: this.error };
            }
        },

        async UpdateObject(form) {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.post('/api/landlord/UpdateObject', { form: form });

                if (response.data.result === 'good') {
                    return { success: true, object: response.data.object };
                } else {
                    this.error = response.data.message || 'Ошибка обновления объекта';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Ошибка обновления объекта';
                this.error = msg;
                return { success: false, error: msg, errors: err.response?.data?.errors };
            } finally {
                this.loading = false;
            }
        },

        async DeleteObject(id) {
            try {
                const response = await axios.post('/api/landlord/DeleteObject', { id: id });

                if (response.data.result === 'good') {
                    return { success: true };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка удаления объекта' };
                }
            } catch (err) {
                const msg = err.response?.data?.error || err.response?.data?.message || 'Ошибка удаления объекта';
                return { success: false, error: msg };
            }
        },

        async uploadObjectMedia(formData) {
            try {
                const response = await axios.post('/api/landlord/uploadObjectMedia', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (response.data.result === 'good') {
                    return {
                        success: true,
                        url: response.data.url,
                        urls: response.data.urls || [response.data.url],
                        file_name: response.data.file_name,
                        size: response.data.size,
                        type: response.data.type
                    };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка загрузки файла' };
                }
            } catch (err) {
                return {
                    success: false,
                    error: err.response?.data?.error || err.response?.data?.message || 'Ошибка загрузки файла'
                };
            }
        },

        // ─── Учёт рабочих часов ───────────────────────────────────────────────

        async logWorkHours(form) {
            try {
                const response = await axios.post('/api/tenant/logWorkHours', form);
                if (response.data.result === 'good')
                    return { success: true, log: response.data.log };
                else {
                    this.error = response.data.error || 'Ошибка записи часов';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка записи часов';
                return { success: false, error: this.error };
            }
        },

        async fetchWorkLogs(periodYear, periodMonth) {
            try {
                const response = await axios.post('/api/tenant/fetchWorkLogs', {
                    period_year: periodYear,
                    period_month: periodMonth
                });
                if (response.data.result === 'good')
                    return { success: true, logs: response.data.logs };
                else {
                    this.error = response.data.error || 'Ошибка получения журнала';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка получения журнала';
                return { success: false, error: this.error };
            }
        },

        // ─── Расчёт ЗП и налогов ─────────────────────────────────────────────

        async calculatePayroll(periodYear, periodMonth) {
            try {
                const response = await axios.post('/api/tenant/calculatePayroll', {
                    period_year: periodYear,
                    period_month: periodMonth
                });
                if (response.data.result === 'good')
                    return { success: true, payrolls: response.data.payrolls, count: response.data.count };
                else {
                    this.error = response.data.error || 'Ошибка расчёта';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка расчёта';
                return { success: false, error: this.error };
            }
        },

        async fetchPayrolls(periodYear, periodMonth) {
            try {
                const response = await axios.post('/api/tenant/fetchPayrolls', {
                    period_year: periodYear,
                    period_month: periodMonth
                });
                if (response.data.result === 'good')
                    return { success: true, payrolls: response.data.payrolls };
                else {
                    this.error = response.data.error || 'Ошибка получения ведомости';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка получения ведомости';
                return { success: false, error: this.error };
            }
        },

        async confirmPayroll(periodYear, periodMonth) {
            try {
                const response = await axios.post('/api/tenant/confirmPayroll', {
                    period_year: periodYear,
                    period_month: periodMonth
                });
                if (response.data.result === 'good')
                    return { success: true, updated: response.data.updated };
                else {
                    this.error = response.data.error || 'Ошибка подтверждения';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка подтверждения';
                return { success: false, error: this.error };
            }
        },

        // ─── Коммунальные услуги и счетчики ─────────────────────────────────

        async fetchUtilityServices(objectId) {
            try {
                const response = await axios.post('/api/tenant/fetchUtilityServices', { objectId });
                if (response.data.result === 'good') {
                    return { success: true, services: response.data.services };
                } else {
                    this.error = response.data.error || 'Ошибка загрузки услуг';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка загрузки услуг';
                return { success: false, error: this.error };
            }
        },

        async saveUtilityService(payload) {
            try {
                const response = await axios.post('/api/tenant/saveUtilityService', payload);
                if (response.data.result === 'good') {
                    return { success: true, service: response.data.service };
                } else {
                    this.error = response.data.error || 'Ошибка сохранения услуги';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка сохранения услуги';
                return { success: false, error: this.error };
            }
        },

        async deleteUtilityService(serviceId) {
            try {
                const response = await axios.post('/api/tenant/deleteUtilityService', { serviceId });
                if (response.data.result === 'good') {
                    return { success: true };
                } else {
                    this.error = response.data.error || 'Ошибка удаления услуги';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка удаления услуги';
                return { success: false, error: this.error };
            }
        },

        async submitUtilityReadings(payload) {
            try {
                const response = await axios.post('/api/tenant/submitUtilityReadings', payload);
                if (response.data.result === 'good') {
                    return { success: true, readings: response.data.readings };
                } else {
                    this.error = response.data.error || 'Ошибка передачи показаний';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка передачи показаний';
                return { success: false, error: this.error };
            }
        },

        async fetchUtilityHistory(objectId) {
            try {
                const response = await axios.post('/api/tenant/fetchUtilityHistory', { objectId });
                if (response.data.result === 'good') {
                    return { success: true, history: response.data.history };
                } else {
                    this.error = response.data.error || 'Ошибка получения истории';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка получения истории';
                return { success: false, error: this.error };
            }
        },

        async uploadTenantUtilityReceipt(formData) {
            try {
                const response = await axios.post('/api/tenant/uploadUtilityReceipt', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (response.data.result === 'good') {
                    return {
                        success: true,
                        message: response.data.message,
                        receipt_url: response.data.receipt_url
                    };
                } else {
                    this.error = response.data.error || 'Ошибка загрузки чека';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка загрузки чека';
                return { success: false, error: this.error };
            }
        },

        // ─── ЖКУ Арендодателя (Дашборд, модерация, услуги) ──────────────────

        async fetchLandlordUtilityDashboard() {
            try {
                const response = await axios.get('/api/landlord/utilityDashboard');
                if (response.data.result === 'good') {
                    return { success: true, stats: response.data.stats };
                } else {
                    this.error = response.data.error || 'Ошибка загрузки аналитики ЖКУ';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка загрузки аналитики ЖКУ';
                return { success: false, error: this.error };
            }
        },

        async fetchUtilities() {
            return await this.fetchLandlordUtilityDashboard();
        },

        async fetchLandlordUtilityReadings(params = {}) {
            try {
                const response = await axios.get('/api/landlord/utilityReadings', { params });
                if (response.data.result === 'good') {
                    return {
                        success: true,
                        readings: response.data.readings,
                        grouped: response.data.grouped
                    };
                } else {
                    this.error = response.data.error || 'Ошибка загрузки начислений';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка загрузки начислений';
                return { success: false, error: this.error };
            }
        },

        async approveUtilityReading(params) {
            try {
                const response = await axios.post('/api/landlord/approveUtilityReadings', params);
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка подтверждения показаний';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка подтверждения показаний';
                return { success: false, error: this.error };
            }
        },

        async rejectUtilityReading(params) {
            try {
                const response = await axios.post('/api/landlord/rejectUtilityReadings', params);
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка отклонения показаний';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка отклонения показаний';
                return { success: false, error: this.error };
            }
        },

        async approveUtilityPayment(params) {
            try {
                const response = await axios.post('/api/landlord/approveUtilityPayment', params);
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка подтверждения оплаты';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка подтверждения оплаты';
                return { success: false, error: this.error };
            }
        },

        async rejectUtilityPayment(params) {
            try {
                const response = await axios.post('/api/landlord/rejectUtilityPayment', params);
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка отклонения оплаты';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка отклонения оплаты';
                return { success: false, error: this.error };
            }
        },

        async fetchLandlordUtilityServices(objectId = 'all') {
            try {
                const response = await axios.get('/api/landlord/utilityServicesList', {
                    params: { object_id: objectId }
                });
                if (response.data.result === 'good') {
                    return { success: true, services: response.data.services };
                } else {
                    this.error = response.data.error || 'Ошибка получения услуг';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка получения услуг';
                return { success: false, error: this.error };
            }
        },

        async saveLandlordUtilityService(payload) {
            try {
                const response = await axios.post('/api/landlord/saveUtilityServiceCustom', payload);
                if (response.data.result === 'good') {
                    return { success: true, service: response.data.service, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка сохранения услуги';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка сохранения услуги';
                return { success: false, error: this.error };
            }
        },

        async deleteLandlordUtilityService(serviceId) {
            try {
                const response = await axios.post('/api/landlord/deleteUtilityServiceCustom', { id: serviceId });
                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                } else {
                    this.error = response.data.error || 'Ошибка удаления услуги';
                    return { success: false, error: this.error };
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Ошибка удаления услуги';
                return { success: false, error: this.error };
            }
        },

        // ─── ФИНАНСЫ АРЕНДАТОРА ─────────────────────────────────────────────────

        async fetchFinanceSummary(params = {}) {
            try {
                const response = await axios.get('/api/tenant/finance/summary', { params });
                if (response.data.result === 'good') {
                    return { success: true, summary: response.data.summary, period: response.data.period };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка загрузки сводки' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка загрузки сводки' };
            }
        },

        async fetchExpenses(params = {}) {
            try {
                const response = await axios.get('/api/tenant/finance/expenses', { params });
                if (response.data.result === 'good') {
                    return { success: true, expenses: response.data.expenses, total: response.data.total };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка загрузки расходов' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка загрузки расходов' };
            }
        },

        async createExpense(formData) {
            try {
                const response = await axios.post('/api/tenant/finance/expenses', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (response.data.result === 'good') {
                    return { success: true, expense: response.data.expense };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка создания записи' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка создания записи' };
            }
        },

        async updateExpense(id, formData) {
            try {
                // Laravel не поддерживает FormData в PUT напрямую, используем POST с _method=PUT
                formData.append('_method', 'PUT');
                const response = await axios.post(`/api/tenant/finance/expenses/${id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (response.data.result === 'good') {
                    return { success: true, expense: response.data.expense };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка обновления записи' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка обновления записи' };
            }
        },

        async deleteExpense(id) {
            try {
                const response = await axios.delete(`/api/tenant/finance/expenses/${id}`);
                if (response.data.result === 'good') {
                    return { success: true };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка удаления записи' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка удаления записи' };
            }
        },

        async fetchFinanceChart(params = {}) {
            try {
                const response = await axios.get('/api/tenant/finance/chart', { params });
                if (response.data.result === 'good') {
                    return { success: true, chart: response.data.chart, has_data: response.data.has_data, periods: response.data.periods };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка загрузки графиков' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка загрузки графиков' };
            }
        },

        async calculateFinancePayroll(params = {}) {
            try {
                const response = await axios.post('/api/tenant/finance/payroll/calculate', params);
                if (response.data.result === 'good') {
                    return { success: true, payrolls: response.data.payrolls, count: response.data.count };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка расчёта ведомости' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка расчёта ведомости' };
            }
        },

        async updateFinancePayrollItem(id, data = {}) {
            try {
                const response = await axios.put(`/api/tenant/finance/payroll/${id}`, data);
                if (response.data.result === 'good') {
                    return { success: true, payroll: response.data.payroll };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка обновления записи' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка обновления записи' };
            }
        },

        async confirmFinancePayroll(params = {}) {
            try {
                const response = await axios.post('/api/tenant/finance/payroll/confirm', params);
                if (response.data.result === 'good') {
                    return { success: true, updated: response.data.updated };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка подтверждения ведомости' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка подтверждения ведомости' };
            }
        },

        // ─── КАБИНЕТ СОТРУДНИКА (EMPLOYER PORTAL) ───────────────────────────
        async fetchEmployerDashboard(params = {}) {
            try {
                const response = await axios.get('/api/employer/dashboard', { params });
                if (response.data.result === 'good') {
                    return {
                        success: true,
                        employee: response.data.employee,
                        active_shift: response.data.active_shift,
                        shifts: response.data.shifts || [],
                        summary: response.data.summary || {},
                    };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка загрузки дашборда' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка соединения с сервером' };
            }
        },

        async startEmployerShift(data = {}) {
            try {
                const response = await axios.post('/api/employer/shifts/start', data);
                if (response.data.result === 'good') {
                    return { success: true, shift: response.data.shift, message: response.data.message };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка открытия смены' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка открытия смены' };
            }
        },

        async endEmployerShift(data = {}) {
            try {
                const response = await axios.post('/api/employer/shifts/end', data);
                if (response.data.result === 'good') {
                    return { success: true, shift: response.data.shift, message: response.data.message };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка закрытия смены' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка закрытия смены' };
            }
        },

        async fetchEmployerShifts(params = {}) {
            try {
                const response = await axios.get('/api/employer/shifts', { params });
                if (response.data.result === 'good') {
                    return {
                        success: true,
                        shifts: response.data.shifts || [],
                        stats: response.data.stats || {},
                    };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка загрузки смен' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка соединения с сервером' };
            }
        },

        async fetchEmployerChatMessages() {
            try {
                const response = await axios.get('/api/employer/chat/messages');
                if (response.data.result === 'good') {
                    return {
                        success: true,
                        messages: response.data.messages || [],
                        companion: response.data.companion || null,
                    };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка загрузки сообщений' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка загрузки сообщений' };
            }
        },

        async sendEmployerChatMessage(payload) {
            try {
                let response;
                if (payload instanceof FormData) {
                    response = await axios.post('/api/employer/chat/send', payload, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                } else {
                    response = await axios.post('/api/employer/chat/send', payload);
                }

                if (response.data.result === 'good') {
                    return { success: true, message: response.data.message };
                } else {
                    return { success: false, error: response.data.error || 'Ошибка отправки сообщения' };
                }
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка отправки сообщения' };
            }
        },

        async fetchNotifications() {
            try {
                const response = await axios.get('/api/notifications');
                if (response.data.result === 'good') {
                    return {
                        success: true,
                        notifications: response.data.notifications || [],
                        unread_count: response.data.unread_count || 0,
                    };
                }
                return { success: false, error: response.data.error };
            } catch (err) {
                return { success: false, error: err.response?.data?.error || 'Ошибка загрузки уведомлений' };
            }
        },

        async markNotificationRead(id) {
            try {
                const response = await axios.post(`/api/notifications/${id}/read`);
                return { success: true, unread_count: response.data.unread_count };
            } catch (err) {
                return { success: false };
            }
        },

        async markAllNotificationsRead() {
            try {
                const response = await axios.post('/api/notifications/read-all');
                return { success: true };
            } catch (err) {
                return { success: false };
            }
        },

        async deleteNotification(id) {
            try {
                const response = await axios.delete(`/api/notifications/${id}`);
                return { success: true, unread_count: response.data.unread_count };
            } catch (err) {
                return { success: false };
            }
        },

        async fetchHeaderSummary() {
            try {
                const response = await axios.get('/api/header/summary');
                if (response.data.result === 'good') {
                    return {
                        success: true,
                        unread_notifications: response.data.unread_notifications || 0,
                        recent_tickets: response.data.recent_tickets || [],
                        unread_messages_count: response.data.unread_messages_count || 0,
                    };
                }
                return { success: false };
            } catch (err) {
                return { success: false };
            }
        }

    }
});